import React from 'react';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import FormLoadingSpinner from '../Common/FormLoadingSpinner';

const ReferenceCheckboxList = ({ items, selectedIds, onToggle, emptyMessage, getDate }) => {
  if (!items.length) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
      {items.map((entry) => {
        const isSelected = selectedIds.includes(entry.id);
        return (
          <label
            key={entry.id}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              isSelected ? 'bg-teal-50 border-teal-300' : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onToggle(entry.id, e.target.checked, entry)}
              className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900 font-mono">{entry.code}</span>
                <span className="text-sm text-gray-600 shrink-0">
                  {entry.total_amount != null
                    ? `S/ ${Number(entry.total_amount).toLocaleString('es-PE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : ''}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {entry.client?.name || entry.supplier?.name || 'Sin cliente'}
                {getDate(entry) ? ` • ${formatDateLocaleLima(getDate(entry))}` : ''}
                {entry.has_mixed_events ? (
                  <span className="block mt-0.5 text-amber-700">
                    Mixta: solo se importarán {entry.venta_items_count ?? 0} ítem(s) con evento venta
                  </span>
                ) : null}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

const VentasOrderReferenceSelectorModal = ({
  isOpen,
  onClose,
  onAccept,
  loading = false,
  quotes = [],
  continuousServices = [],
  exitGuides = [],
  selectedQuoteIds = [],
  selectedContinuousServiceIds = [],
  selectedExitGuideIds = [],
  onToggleQuote,
  onToggleContinuousService,
  onToggleExitGuide,
  clientName = '',
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Seleccionar referencias
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-gray-500">
              <FormLoadingSpinner message="Cargando referencias…" hint="" compact />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-6">
                Vincule cotizaciones, servicios continuos de ventas o guías de salida con ítems de evento
                venta como referencia. Las cotizaciones y servicios continuos no copian ítems; las guías de
                salida importan solo productos con evento venta (si tienen precio en el punto de venta). En
                guías mixtas (venta + consumo) se omiten los de consumo.
                {clientName ? (
                  <span className="block mt-1 text-teal-800 font-medium">Cliente: {clientName}</span>
                ) : null}
              </p>

              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-2">Cotizaciones emitidas</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Cotizaciones del punto de venta actual. Marque las que desea referenciar.
                </p>
                <ReferenceCheckboxList
                  items={quotes}
                  selectedIds={selectedQuoteIds}
                  onToggle={onToggleQuote}
                  emptyMessage="No hay cotizaciones emitidas disponibles"
                  getDate={(entry) => entry.issue_date}
                />
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-2">Servicios continuos de ventas</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Servicios continuos abiertos. Marque los que desea referenciar en esta venta.
                </p>
                <ReferenceCheckboxList
                  items={continuousServices}
                  selectedIds={selectedContinuousServiceIds}
                  onToggle={onToggleContinuousService}
                  emptyMessage="No hay servicios continuos abiertos disponibles"
                  getDate={(entry) => entry.start_date}
                />
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-2">Guías de salida (evento venta)</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Guías de salida emitidas con al menos un ítem de evento venta (pueden ser mixtas). Al
                  aceptar, solo se importan los productos con evento venta y precio en este punto de venta.
                </p>
                <ReferenceCheckboxList
                  items={exitGuides}
                  selectedIds={selectedExitGuideIds}
                  onToggle={onToggleExitGuide}
                  emptyMessage="No hay guías de salida con ítems de evento venta disponibles"
                  getDate={(entry) => entry.issue_date || entry.entry_date}
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  onClick={onAccept}
                  className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  Aceptar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VentasOrderReferenceSelectorModal;
